import React, { useState } from 'react';
import { X, Paperclip, Link2, Mic, Plus, Download, FileText, PenLine, LayoutTemplate, Loader2, Sparkles } from 'lucide-react';
import { suggestionTags, createCards } from '../mock';
import { AIAPI, AssetsAPI } from '../api';
import { AssistantOrb } from './Brand';
import { ASSISTANT_NAME, SIGNED_IN_PRODUCT_NAME } from '../config/brand';

const iconMap = {
  plus: Plus,
  download: Download,
  'file-text': FileText,
  pen: PenLine,
  'layout-template': LayoutTemplate,
};

const AssistantHero = () => (
  <div className="relative w-[180px] h-[180px] shrink-0 flex items-center justify-center">
    <div className="absolute inset-4 rounded-full border border-[#0F766E]/20 animate-[spin_18s_linear_infinite]" style={{ borderTopColor: '#0F766E' }} />
    <div className="absolute inset-9 rounded-full border border-[#0A1551]/15" />
    <AssistantOrb size={92} />
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-white shadow text-[11px] font-semibold text-[#0A1551]">{ASSISTANT_NAME}</div>
  </div>
);

const CreateModal = ({ open, onClose, teamId, onAssetCreated }) => {
  const [prompt, setPrompt] = useState('');
  const [dontShow, setDontShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleGenerate = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setError('');
    try {
      const asset = await AIAPI.generateForm(prompt.trim(), teamId || null);
      onAssetCreated && onAssetCreated(asset);
      setPrompt('');
      onClose();
    } catch (e) {
      setError('Failed to generate form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCreate = async (cardId) => {
    const nameMap = {
      scratch: 'Untitled Form',
      import: 'Imported Form',
      pdf: 'PDF-Based Form',
      sign: 'Signature Request',
      template: 'Template Form',
    };
    try {
      const asset = await AssetsAPI.create({ name: nameMap[cardId] || 'Untitled Form', type: cardId === 'sign' ? 'Sign' : 'Form', team_id: teamId || null });
      onAssetCreated && onAssetCreated(asset);
      onClose();
    } catch (e) {
      setError('Failed to create.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0A1551]/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[920px] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Gradient background */}
        <div className="bg-gradient-to-br from-[#E6F4F2] via-[#F5F6FB] to-[#E8ECFF] p-8 md:p-10">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-[#0A1551]" />
          </button>

          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10 mb-6">
            <AssistantHero />
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-[36px] md:text-[42px] font-extrabold text-[#0A1551] leading-tight mb-2">
                Describe your form
              </h2>
              <p className="text-[18px] text-[#4A517D]">
                {SIGNED_IN_PRODUCT_NAME} AI drafts it for you
              </p>
            </div>
          </div>

          {/* AI prompt input */}
          <div className="bg-white rounded-2xl p-1.5 shadow-[0_0_0_2px_rgba(15,118,110,0.5),0_20px_50px_-15px_rgba(15,118,110,0.25)]">
            <div className="p-4">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Create a registration form for new customers"
                className="w-full min-h-[64px] resize-none border-0 focus:outline-none text-[16px] text-[#0A1551] placeholder:text-[#9599C0]"
              />
            </div>
            <div className="flex items-center justify-between px-3 pb-2">
              <div className="flex items-center gap-2">
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E3E5F5] text-[13px] font-medium text-[#0A1551] hover:bg-[#F5F6FB] transition-colors">
                  <Paperclip className="w-3.5 h-3.5" />
                  Upload
                </button>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E3E5F5] text-[13px] font-medium text-[#0A1551] hover:bg-[#F5F6FB] transition-colors">
                  <Link2 className="w-3.5 h-3.5" />
                  Import URL
                </button>
              </div>
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim() || loading}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="ai-generate-button"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {loading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-3 text-center text-[13px] text-[#E5387B] font-medium">{error}</div>
          )}

          {/* Suggestion tags */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            {suggestionTags.slice(0, 4).map((tag) => (
              <button
                key={tag}
                onClick={() => setPrompt(`Create a ${tag.toLowerCase()}`)}
                className="px-4 py-1.5 rounded-full bg-white/80 hover:bg-white border border-[#C7E3DF] text-[13px] text-[#0F766E] font-medium transition-colors"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Bottom cards */}
        <div className="bg-white px-6 md:px-10 pt-6 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {createCards.map((card) => {
              const Icon = iconMap[card.icon];
              return (
                <button
                  key={card.id}
                  onClick={() => handleQuickCreate(card.id)}
                  className="group text-center"
                >
                  <div className={`aspect-[4/3] rounded-lg ${card.color} flex items-center justify-center mb-3 group-hover:scale-[1.03] transition-transform relative overflow-hidden`}>
                    {card.id === 'scratch' ? (
                      <Plus className="w-10 h-10 text-[#7C8AC7]" strokeWidth={2} />
                    ) : (
                      <MiniFormPreview color={card.preview} type={card.id} />
                    )}
                  </div>
                  <div className="text-[13px] font-semibold text-[#0A1551] leading-tight">{card.title}</div>
                  <div className="text-[12px] text-[#6F76A7] mt-1 leading-tight">{card.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer checkbox */}
        <div className="bg-white pb-5 flex justify-center">
          <label className="flex items-center gap-2 text-[13px] text-[#6F76A7] cursor-pointer">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="w-4 h-4 rounded border-[#D5D7ED] accent-[#0A1551]"
            />
            Don&apos;t show this again
          </label>
        </div>
      </div>
    </div>
  );
};

const MiniFormPreview = ({ color, type }) => {
  const colors = {
    blue: { bg: 'bg-[#3B82F6]', accent: '#1D4ED8' },
    red: { bg: 'bg-[#EF4444]', accent: '#B91C1C' },
    green: { bg: 'bg-[#10B981]', accent: '#047857' },
    orange: { bg: 'bg-[#F97316]', accent: '#C2410C' },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Back paper */}
      <div className="absolute w-[55%] h-[75%] bg-white rounded shadow-md rotate-[-8deg] left-[15%] top-[10%] p-1.5">
        <div className="h-1 w-2/3 bg-slate-200 rounded mb-1"></div>
        <div className="h-1 w-1/2 bg-slate-200 rounded"></div>
      </div>
      {/* Front paper */}
      <div className={`absolute w-[55%] h-[75%] rounded shadow-md rotate-[6deg] right-[10%] top-[15%] overflow-hidden ${type === 'template' ? c.bg : 'bg-white'} `}>
        {type === 'template' ? (
          <>
            <div className="h-2 w-full bg-black/20"></div>
            <div className="p-1.5 space-y-1">
              <div className="h-1 w-2/3 bg-white/70 rounded"></div>
              <div className="h-1 w-1/2 bg-white/70 rounded"></div>
            </div>
          </>
        ) : (
          <div className="p-1.5 space-y-1">
            <div className={`h-1 w-2/3 rounded`} style={{ background: c.accent }}></div>
            <div className="h-1 w-1/2 bg-slate-300 rounded"></div>
            <div className={`h-1 w-3/4 rounded`} style={{ background: c.accent }}></div>
          </div>
        )}
      </div>
      {type === 'pdf' && (
        <div className="absolute top-1 right-1 bg-red-500 text-white text-[8px] font-bold px-1 rounded">PDF</div>
      )}
    </div>
  );
};

export default CreateModal;
